import {gql} from '@apollo/client';
import {DocumentNode} from 'graphql';

import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import QueryWrapper from 'graphql-client/QueryWrapper';
import type {AaSequenceAminoAcidsQuery} from 'model-hub/__generated/AaSequenceAminoAcidsQuery';
import type {AaSequenceStructurePredictionPermissionsQuery} from 'model-hub/__generated/AaSequenceStructurePredictionPermissionsQuery';
import type {MoleculeHeavyAtomCountQuery} from 'model-hub/__generated/MoleculeHeavyAtomCountQuery';
import type {NewPredictionAaSequenceInputsQuery} from 'model-hub/__generated/NewPredictionAaSequenceInputsQuery';
import type {NewPredictionMoleculeInputsQuery} from 'model-hub/__generated/NewPredictionMoleculeInputsQuery';

export const CURRENT_USER_AI_CREDIT_USAGE_QUERY = gql`
  query CurrentUserAiCreditUsageQuery @skipAudit @engComponent(name: MODEL_HUB) {
    currentUserAiCreditUsage {
      hasEntitlement
      creditsUsed
      creditsGranted
    }
  }
`;

export const MOLECULE_HEAVY_ATOM_COUNT_QUERY = gql`
  query MoleculeHeavyAtomCountQuery($moleculeIds: [StrictString!]!)
  @skipAudit
  @engComponent(name: CHEMISTRY) {
    molecules(ids: $moleculeIds) {
      ... on MoleculeNode {
        id
        folder {
          id
        }
        currentVersion {
          id
          chemicalStructure {
            id
            heavyAtomCount {
              ... on IntPropertyNode {
                value
              }
            }
          }
        }
        permissions {
          readable
        }
      }
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

export const MOLECULE_HEAVY_ATOM_COUNT_QUERY_WRAPPER: QueryWrapper<
  MoleculeHeavyAtomCountQuery,
  {moleculeIds: Array<string>}
> = {
  query: MOLECULE_HEAVY_ATOM_COUNT_QUERY,
};

export const PREDICTION_JOB_RESULTS_DOWNLOAD_URL_QUERY = gql`
  query PredictionJobResultsDownloadUrlQuery($id: StrictString!) @skipAudit @engComponent(name: MODEL_HUB) {
    predictionJob(id: $id) {
      id
      resultsDownloadUrl
    }
  }
`;

export const PREDICTION_JOB_PROPERTIES_QUERY = gql`
  query PredictionJobPropertiesQuery($id: StrictString!) @skipAudit @engComponent(name: MODEL_HUB) {
    predictionJob(id: $id) {
      id
      properties
    }
  }
`;

export const PREDICTION_BATCH_DETAIL_QUERY = gql`
  query PredictionBatchDetailQuery($id: StrictString!) @skipAudit @engComponent(name: MODEL_HUB) {
    predictionBatch(id: $id) {
      id
      name
      createdAt
      modifiedAt
      modelName
      statusCode
      numPredictionJobs
      creator {
        id
        name
      }
    }
  }
`;

export const PREDICTION_DETAIL_QUERY = gql`
  query PredictionDetailQuery($id: StrictString!) @skipAudit @engComponent(name: MODEL_HUB) {
    predictionJob(id: $id) {
      id
      name
      createdAt
      modifiedAt
      modelName
      status
      componentApiIds
      configJson
      hasResultsFile
      resultsDownloadUrl
      standardizedOutputFileName
      standardizedOutputDownloadUrl
      predictionBatch {
        id
        name
      }
    }
  }
`;

export const PREDICTION_JOB_MOLECULE_SMILES_QUERY = gql`
  query PredictionJobMoleculeSmilesQuery($moleculeIds: [StrictString!]!)
  @skipAudit
  @engComponent(name: CHEMISTRY) {
    molecules(ids: $moleculeIds) {
      ... on MoleculeNode {
        id
        currentVersion {
          id
          chemicalStructure {
            id
            canonicalSmiles
          }
        }
      }
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

export const AA_SEQUENCE_AMINO_ACIDS_QUERY: DocumentNode = gql`
  query AaSequenceAminoAcidsQuery($ids: [StrictString!]!) @skipAudit @engComponent(name: MOLBIO) {
    aaSequences(ids: $ids) {
      ... on AaSequenceNode {
        id
        folder {
          id
        }
        experimentalCurrentVersion {
          dbId
          aminoAcids
        }
        permissions {
          readable
          polymerFeaturesEditable
          structurePredictionEditable
        }
      }
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

export const AA_SEQUENCE_AMINO_ACIDS_QUERY_WRAPPER: QueryWrapper<
  AaSequenceAminoAcidsQuery,
  {ids: Array<string>}
> = {
  query: AA_SEQUENCE_AMINO_ACIDS_QUERY,
};

export const AA_SEQUENCE_STRUCTURE_PREDICTION_PERMISSIONS_QUERY: DocumentNode = gql`
  query AaSequenceStructurePredictionPermissionsQuery($ids: [StrictString!]!)
  @skipAudit
  @engComponent(name: MOLBIO) {
    aaSequences(ids: $ids) {
      __typename
      ... on AaSequenceNode {
        id
        permissions {
          structurePredictionEditable
        }
      }
    }
  }
`;

export const AA_SEQUENCE_STRUCTURE_PREDICTION_PERMISSIONS_QUERY_WRAPPER: QueryWrapper<
  AaSequenceStructurePredictionPermissionsQuery,
  {ids: Array<string>}
> = {
  query: AA_SEQUENCE_STRUCTURE_PREDICTION_PERMISSIONS_QUERY,
};

/** What New prediction needs to turn a picked AA sequence into an input component. */
export const NEW_PREDICTION_AA_SEQUENCE_INPUTS_QUERY: DocumentNode = gql`
  query NewPredictionAaSequenceInputsQuery($ids: [StrictString!]!) @skipAudit @engComponent(name: MOLBIO) {
    aaSequences(ids: $ids) {
      ... on AaSequenceNode {
        id
        name
        experimentalCurrentVersion {
          dbId
          aminoAcids
        }
        permissions {
          readable
          structurePredictionEditable
        }
      }
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

export const NEW_PREDICTION_AA_SEQUENCE_INPUTS_QUERY_WRAPPER: QueryWrapper<
  NewPredictionAaSequenceInputsQuery,
  {ids: Array<string>}
> = {
  query: NEW_PREDICTION_AA_SEQUENCE_INPUTS_QUERY,
};

/** What New prediction needs to turn a picked molecule into a ligand component. */
export const NEW_PREDICTION_MOLECULE_INPUTS_QUERY: DocumentNode = gql`
  query NewPredictionMoleculeInputsQuery($moleculeIds: [StrictString!]!)
  @skipAudit
  @engComponent(name: CHEMISTRY) {
    molecules(ids: $moleculeIds) {
      ... on MoleculeNode {
        id
        name
        currentVersion {
          id
          chemicalStructure {
            id
            canonicalSmiles
          }
        }
        permissions {
          readable
        }
      }
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

export const NEW_PREDICTION_MOLECULE_INPUTS_QUERY_WRAPPER: QueryWrapper<
  NewPredictionMoleculeInputsQuery,
  {moleculeIds: Array<string>}
> = {
  query: NEW_PREDICTION_MOLECULE_INPUTS_QUERY,
};
